import { Table, Button, Tag, Typography } from 'antd';
import { useStoreState, useStoreActions } from '../../store';
import { FormOutlined } from '@ant-design/icons';
import { MODAL_KEY } from './EditPackagingSetModal';
import type { PackagingSetV2 } from '../../store/services/PartnerService/types';
const { Paragraph } = Typography;

export const PackagingSetsTable = () => {
  // Data is fetched by PackagingConfiguration: this table sits inside <Card loading>, so fetching on mount would loop.
  const partnerPackaging = useStoreState((state) => state.packagingV2.partnerPackagingV2);
  const partnerPackagingSets = useStoreState((state) => state.packagingV2.partnerPackagingSets);
  const canEdit = useStoreState((state) => state.session.canEdit);
  const toggleModal = useStoreActions((action) => action.navigation.toggleModal);

  const openModal = (activePackagingSet: PackagingSetV2) => {
    toggleModal({ open: true, key: MODAL_KEY, params: activePackagingSet });
  };

  const packagingSetColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value: any, record: PackagingSetV2) => <Paragraph>{record.name}</Paragraph>,
      sorter: (a: PackagingSetV2, b: PackagingSetV2) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (value: any, record: PackagingSetV2) => <Paragraph>{record.description}</Paragraph>,
      sorter: (a: PackagingSetV2, b: PackagingSetV2) => {
        if (!a.description && b.description) {
          return 1;
        }
        if (!b.description && a.description) {
          return -1;
        }
        if (!b.description || !a.description) {
          return 0;
        }
        if (a.description < b.description) {
          return -1;
        }
        if (a.description > b.description) {
          return 1;
        }
        return 0;
      },
    },
    {
      title: 'Packaging Set ID',
      dataIndex: 'packagingSetId',
      render: (value: any, record: PackagingSetV2) => (
        <Paragraph copyable={{ text: record.packagingSetId }}>{record.packagingSetId}</Paragraph>
      ),
      sorter: (a: PackagingSetV2, b: PackagingSetV2) => {
        if (!a.packagingSetId && b.packagingSetId) {
          return 1;
        }
        if (!b.packagingSetId && a.packagingSetId) {
          return -1;
        }
        if (!b.packagingSetId || !a.packagingSetId) {
          return 0;
        }
        if (a.packagingSetId < b.packagingSetId) {
          return -1;
        }
        if (a.packagingSetId > b.packagingSetId) {
          return 1;
        }
        return 0;
      },
    },
    {
      title: 'Packaging Set ID Alias',
      dataIndex: 'partnerProvidedId',
      render: (value: any, record: PackagingSetV2) => (
        <Paragraph copyable={{ text: record.partnerProvidedId }}>{record.partnerProvidedId}</Paragraph>
      ),
      sorter: (a: PackagingSetV2, b: PackagingSetV2) => {
        if (!a.partnerProvidedId && b.partnerProvidedId) {
          return 1;
        }
        if (!b.partnerProvidedId && a.partnerProvidedId) {
          return -1;
        }
        if (!b.partnerProvidedId || !a.partnerProvidedId) {
          return 0;
        }
        if (a.partnerProvidedId < b.partnerProvidedId) {
          return -1;
        }
        if (a.partnerProvidedId > b.partnerProvidedId) {
          return 1;
        }
        return 0;
      },
    },
    {
      title: 'Packaging',
      dataIndex: 'packaging',
      align: 'center',
      responsive: ['lg'],
      //filters: fcFilters,
      onFilter: (value: any, record: PackagingSetV2) => {
        return record.packagingIds?.includes(value);
      },
      render: (value: any, record: PackagingSetV2) => {
        if (!record?.packagingIds) {
          return;
        }
        return (
          <span>
            {record.packagingIds.map((pckgId) => (
              <Tag key={`${pckgId}-tag`}>
                {partnerPackaging?.find((partnerPackage) => partnerPackage.packagingId === pckgId)?.name ?? pckgId}
              </Tag>
            ))}
          </span>
        );
      },
    },
    ...(canEdit
      ? [
          {
            title: 'Edit',
            align: 'center' as const,
            key: 'edit',
            render: (value: any) => {
              return (
                <Button onClick={() => openModal(value)}>
                  Edit <FormOutlined />
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        dataSource={partnerPackagingSets?.filter((packagingSet) => !packagingSet.archived)}
        columns={packagingSetColumns as any}
        rowKey="packagingSetId"
        loading={!partnerPackagingSets}
        pagination={false}
      />
    </>
  );
};
